import { IconButton, TextareaAutosize, Typography } from '@material-ui/core';
import api from 'api/DuvidasFrequentes';
import Context from 'components/__ASSOCIADO__/DuvidasFrequentes/Context';
import { SendIcon, SyncIcon } from 'components/Icons';
import SatisfactionSurvey from 'components/Modal/SatisfactionSurvey';
import { AssemblyType } from 'helpers/enums/Assembleia';
import { ScreenSize } from 'plataforma-fundacao-componentes';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { IAssembleiaAssociadoEvento } from 'store/modules/AssembleiasAssociadoReducer';
import { closeModal, openModal } from 'store/modules/ModalActions';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import { isMobile } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import useStyle from './style';
import { TextRange } from 'helpers/utils/StringUtils';
import translation from 'locales/translation';

interface Props {
  evento: IAssembleiaAssociadoEvento;
  usuario: string;
  idEvento: number;
  isDisabled?: boolean;
}

const PERGUNTA_MAXLENGHT = 200;

export default function Perguntar({
  evento,
  usuario,
  idEvento,
  isDisabled,
}: Props) {
  const classes = useStyle();
  const [duvida, setDuvida] = useState<string>('');
  const [isSending, setSending] = useState<boolean>(false);
  const { atualizarPergunta, setDestaque } = useContext(Context);
  const dispatch = useDispatch();

  const canSend = useMemo(() => duvida.trim().length > 0, [duvida]);

  const hanldeChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDuvida(TextRange(event.currentTarget.value, PERGUNTA_MAXLENGHT));
  };

  const handleFocus = () => {
    const chatElement = document.getElementById('duvidas-lista');
    if (!chatElement) return;
    chatElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isSending || !canSend) return;
      setSending(true);
      const { fail, data } = await api.perguntar({
        usuario,
        idEvento,
        duvida,
      });
      if (fail) {
        ErrorHandler(fail);
      }
      if (data) {
        atualizarPergunta(data);
        toast.success(translation.DuvidasFrequentes.sucessoEnvio);
        setDuvida('');
        setDestaque(data.idDuvidaFrequente, false);
      }
      setSending(false);
    },
    [
      usuario,
      idEvento,
      duvida,
      isSending,
      canSend,
      atualizarPergunta,
      setDestaque,
    ]
  );

  const isPresent = useMemo(() => {
    return (
      evento &&
      evento.contasDisponiveis &&
      evento.contasDisponiveis.filter((acc: { cpfCnpj: string }) => {
        return acc.cpfCnpj !== usuario;
      })
    );
  }, [evento, usuario]);

  const countVotos = useMemo(() => {
    return evento?.pautas?.filter((x: any) => {
      return x.votos.length > 0;
    });
  }, [evento?.pautas]);

  const showSurveyButton = useMemo(() => {
    if (
      evento &&
      evento.flagPesquisa &&
      evento.tipo === AssemblyType.AGN &&
      evento.status === 2 &&
      isPresent &&
      countVotos &&
      countVotos.length
    ) {
      return true;
    }
    return false;
  }, [countVotos, evento, isPresent]);

  const satisfactionSurveyKey = getUniqueKey();

  const modalSurvey = useCallback(() => {
    dispatch(
      openModal(SatisfactionSurvey, {
        applicationId: evento.modoEvento,
        eventId: Number(evento && evento.id),
        cpfCnpj: String(usuario),
        modalKey: satisfactionSurveyKey,
        preventEscExit: true,
        preventMaskExit: true,
        mobileOn: ScreenSize.sm,
        onClose: () => closeModal(satisfactionSurveyKey),
      })
    );
  }, [dispatch, evento, satisfactionSurveyKey, usuario]);

  return (
    <footer>
      {evento && evento.flagPesquisa ? (
        <button
          className={classes.button}
          onClick={modalSurvey}
          disabled={!showSurveyButton}
          aria-disabled={!showSurveyButton}
          tabIndex={!showSurveyButton ? -1 : 0}
        >
          <span>{translation.DuvidasFrequentes.pesquisa}</span>
        </button>
      ) : undefined}

      <div className={classes.root}>
        <form
          className={[classes.form, isDisabled ? classes.formIsDisabled : '']
            .join(' ')
            .trim()}
          onSubmit={handleSubmit}
        >
          <TextareaAutosize
            placeholder={
              isMobile()
                ? translation.DuvidasFrequentes.perguntaFormPlaceholder.substring(
                    0,
                    25 - 3
                  ) + '...'
                : translation.DuvidasFrequentes.perguntaFormPlaceholder
            }
            maxRows={4}
            className={classes.textArea}
            value={duvida}
            onChange={hanldeChange}
            onFocus={handleFocus}
            disabled={isDisabled || isSending}
          />

          <Typography
            component='span'
            className={
              duvida.length === PERGUNTA_MAXLENGHT
                ? classes.counterMax
                : classes.counter
            }
          >
            {duvida.length}/{PERGUNTA_MAXLENGHT}
          </Typography>
          <IconButton
            type='submit'
            color='primary'
            className={classes.submit}
            disabled={isDisabled || !canSend || isSending}
          >
            {isSending ? <SyncIcon animated /> : <SendIcon />}
          </IconButton>
        </form>
      </div>
    </footer>
  );
}
